import type { SiteSeverity } from "@/services";
import { severityTextColor, SEVERITY_LABELS } from "./markers";

export interface PopoverSite {
  id: string;
  label: string;
  severity: SiteSeverity;
}

export interface PopoverState {
  x: number;
  y: number;
  sites: PopoverSite[];
  clusterKey?: string;
}

interface Props {
  state: PopoverState;
  containerWidth: number;
  containerHeight: number;
  onSiteClick: (id: string) => void;
  onCctvClick: (id: string) => void;
  onSitePick: (id: string) => void;
}

const BRAND = "#FF7500";
const CONNECTOR = 32;
const RADIUS = 8;

const SINGLE_W = 140;
const SINGLE_H = 53;
const LIST_W = 160;
const ROW_H = 28;
const LIST_PAD_Y = 6;

const EDGE = 4;

function clampTop(top: number, height: number, containerHeight: number) {
  return Math.min(Math.max(top, EDGE), Math.max(containerHeight - height - EDGE, EDGE));
}

export function MapPopover({
  state,
  containerWidth,
  containerHeight,
  onSiteClick,
  onCctvClick,
  onSitePick,
}: Props) {
  const single = state.sites.length === 1 ? state.sites[0] : null;
  const width = single ? SINGLE_W : LIST_W;
  const height = single ? SINGLE_H : LIST_PAD_Y * 2 + state.sites.length * ROW_H;

  const toLeft = state.x > containerWidth / 2;
  const left = toLeft ? state.x - CONNECTOR - width : state.x + CONNECTOR;
  const top = clampTop(
    single ? state.y - height + 1 : state.y - height / 2,
    height,
    containerHeight
  );

  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute z-[900]"
        style={{
          left: toLeft ? state.x - CONNECTOR : state.x,
          top: state.y - 1,
          width: CONNECTOR + RADIUS,
          marginLeft: toLeft ? -RADIUS : 0,
          height: 2,
          background: BRAND,
        }}
      />

      <div
        className="absolute z-[900] overflow-hidden rounded-lg shadow-lg"
        style={{ left, top, width, minHeight: height, background: BRAND }}
      >
        {single ? (
          <div className="flex h-full flex-col justify-between p-1.5">
            <p className="truncate px-0.5 text-center text-[0.75rem] font-semibold leading-tight text-white">
              {single.label}
            </p>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => onSiteClick(single.id)}
                className="h-[1.3rem] flex-1 rounded-full bg-white text-[0.625rem] font-bold hover:bg-neutral-100"
                style={{ color: BRAND }}
              >
                현장관제
              </button>
              <button
                type="button"
                onClick={() => onCctvClick(single.id)}
                className="h-[1.3rem] flex-1 rounded-full bg-white text-[0.625rem] font-bold hover:bg-neutral-100"
                style={{ color: BRAND }}
              >
                AI CCTV
              </button>
            </div>
          </div>
        ) : (
          <ul className="py-1.5">
            {state.sites.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onSitePick(s.id)}
                  className="flex h-[1.75rem] w-full items-center gap-2 px-2 text-left hover:bg-black/10"
                >
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 flex-none rounded-full ring-1 ring-white/70"
                    style={{ background: severityTextColor(s.severity) }}
                  />
                  <span className="min-w-0 flex-1 truncate text-[0.6875rem] font-semibold text-white">
                    {s.label}
                  </span>
                  <span className="flex-none text-[0.625rem] font-medium text-white/80">
                    {SEVERITY_LABELS[s.severity]}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
